// /src/lib/challenges.ts
import { supabase } from './supabase';
import { getCurrentUser } from './auth';

export interface ChallengeSession {
  id: string;
  song_id: string;
  max_participants: number;
  duration_minutes: number;
  reward_points: number;
  expires_at: string;
  status: 'active' | 'completed' | 'expired';
  created_at: string;
  participant_count?: number;
  song?: {
    creator: string;
    song_cover: string;
    language: string;
    is_free: boolean;
  };
}

export interface ChallengeParticipant {
  id: string;
  session_id: string;
  user_id: string;
  score: number;
  completed_at: string | null;
  completion_time: number | null;
  points_earned: number;
  user?: {
    email: string;
    full_name?: string;
    username?: string;
    profile_image?: string;
  };
}

export interface LeaderboardEntry {
  id: string;
  user_id: string;
  total_points: number;
  challenges_completed: number;
  current_streak: number;
  best_streak: number;
  last_activity: string;
  user?: {
    username: string;
    profile_image?: string;
  };
}

export interface UserRank {
  userId: string;
  rank: number;                 // 1-based rank
  totalPoints: number;
  challengesCompleted: number;
  currentStreak: number;
  bestStreak: number;
  lastActivity: string | null;
}

// ---------- Utils ----------

// Fisher–Yates shuffle (optionally seeded)
function shuffleArray<T>(array: T[], seed?: string): T[] {
  const shuffled = [...array];
  const rand = seed ? seedRandom(seed) : Math.random;

  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

// Seeded RNG
function seedRandom(seed: string) {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = ((hash << 5) - hash) + seed.charCodeAt(i);
    hash |= 0; // force 32-bit int
  }
  return function () {
    hash = (hash * 9301 + 49297) % 233280;
    return hash / 233280;
  };
}

// ---------- Question generation ----------

export function generateRandomQuestions(
  lyrics: string,
  userId: string,
  sessionId: string
): Array<{ sentence: string; blankWord: string; options: string[] }> {
  const sentences = lyrics.split(/[.!?]+/).filter(line =>
    line.trim().length > 10 &&
    line.split(' ').length >= 4 &&
    !/chorus|verse/i.test(line)
  );

  // Ensure we have at least 20 sentences for questions
  let finalSentences = sentences;
  if (sentences.length < 20) {
    // If not enough sentences, use more flexible criteria
    const additionalSentences = lyrics.split(/[,;:]+/).filter(line =>
      line.trim().length > 8 &&
      line.split(' ').length >= 3 &&
      !sentences.includes(line.trim())
    );
    finalSentences = [...sentences, ...additionalSentences];
  }

  // If still not enough, repeat sentences with different patterns
  if (finalSentences.length < 20) {
    const repetitions = Math.ceil(20 / finalSentences.length);
    const repeatedSentences: string[] = [];
    for (let i = 0; i < repetitions; i++) {
      repeatedSentences.push(...finalSentences);
    }
    finalSentences = repeatedSentences.slice(0, 20);
  } else {
    finalSentences = finalSentences.slice(0, 20);
  }

  const seed = `${userId}-${sessionId}`;
  const shuffledSentences = shuffleArray(finalSentences, seed);

  const questions = shuffledSentences.map(sentence => {
    const words = sentence.trim().split(' ').filter(word =>
      word.length > 3 &&
      !/^\d+$/.test(word) &&
      !['the', 'and', 'but', 'for', 'are', 'was', 'were', 'been', 'have', 'has', 'had', 'will', 'would', 'could', 'should']
        .includes(word.toLowerCase())
    );

    if (words.length === 0) return null;

    const rand = seedRandom(seed + sentence);
    const blankWord = words[Math.floor(rand() * words.length)].replace(/[^\w]/g, '');

    const options = [blankWord];
    const allWords = lyrics.split(/\s+/).filter(w =>
      w.length > 2 && w !== blankWord && !/^\d+$/.test(w)
    );

    const shuffledOptions = shuffleArray(allWords, seed + blankWord);
    for (const opt of shuffledOptions) {
      const cleaned = opt.replace(/[^\w]/g, '');
      if (!options.includes(cleaned) && cleaned.length > 2) {
        options.push(cleaned);
        if (options.length >= 4) break;
      }
    }

    return {
      sentence: sentence.trim().replace(new RegExp(`\\b${blankWord}\\b`, 'gi'), '____'),
      blankWord,
      options: shuffleArray(options, seed + 'options')
    };
  }).filter(Boolean) as Array<{ sentence: string; blankWord: string; options: string[] }>;

  return questions;
}

// ---------- Challenges ----------

export async function getActiveChallenges(): Promise<ChallengeSession[]> {
  const { data, error } = await supabase
    .from('stripe_challenge_sessions')
    .select(`
      *,
      song:stripesongs(creator, song_cover, language, is_free),
      stripe_challenge_participants(count)
    `)
    .order('created_at', { ascending: false });

  if (error) throw error;

  return (data || []).map((session: any) => ({
    ...session,
    participant_count: session?.stripe_challenge_participants?.[0]?.count || 0,
    song: session.song ? {
      creator: session.song.creator,
      song_cover: session.song.song_cover,
      language: session.song.language,
      is_free: session.song.is_free
    } : undefined
  }));
}

export async function createChallengeSession(
  songId: string,
  maxParticipants: number,
  durationMinutes: number,
  rewardPoints: number
): Promise<ChallengeSession> {
  const user = getCurrentUser();
  if (!user) throw new Error('Usuario no autenticado');

  // Verify admin
  const { data: userData, error: userError } = await supabase
    .from('stripeusers')
    .select('is_admin')
    .eq('id', user.id)
    .single();

  if (userError) throw new Error('Error verificando permisos de usuario');
  if (!userData?.is_admin) throw new Error('Solo los administradores pueden crear desafíos');

  const expiresAtIso = new Date(Date.now() + durationMinutes * 60_000).toISOString();

  // ✅ FIX: include creator_id
  const { data, error } = await supabase
    .from('stripe_challenge_sessions')
    .insert({
      creator_id: user.id,                 // <-- REQUIRED (NOT NULL)
      song_id: songId,
      max_participants: maxParticipants,
      duration_minutes: 720, // 12 hours
      reward_points: rewardPoints,
      expires_at: expiresAtIso,
      status: 'active'
    })
    .select('*')
    .single();

  if (error) {
    // Friendlier hint for the exact error you saw
    if ((error as any).code === '23502' && String((error as any).message).includes('creator_id')) {
      throw new Error('creator_id es NULL. Asegúrate de tener sesión iniciada y de enviar creator_id: user.id.');
    }
    throw error;
  }

  return data as ChallengeSession;
}


export async function joinChallenge(sessionId: string): Promise<void> {
  const user = getCurrentUser();
  if (!user) throw new Error('Usuario no autenticado');

  // Check if challenge exists and is still active
  const { data: session, error: sessionError } = await supabase
    .from('stripe_challenge_sessions')
    .select('*')
    .eq('id', sessionId)
    .single();

  if (sessionError) throw new Error('Desafío no encontrado');
  if (session.status !== 'active') throw new Error('Este desafío ya no está activo');
  if (new Date(session.expires_at) < new Date()) {
    throw new Error('Este desafío ha expirado. Ya no puedes unirte.');
  }

  // Check if user has already participated in this challenge (completed or not)
  const { data: existingParticipant, error: checkError } = await supabase
    .from('stripe_challenge_participants')
    .select('id')
    .eq('session_id', sessionId)
    .eq('user_id', user.id)
    .maybeSingle();

  if (checkError) throw new Error('Error verificando participación');
  if (existingParticipant) throw new Error('Ya has participado en este desafío anteriormente');

  // Check participant count
  const { count: currentParticipants, error: countError } = await supabase
    .from('stripe_challenge_participants')
    .select('*', { count: 'exact', head: true })
    .eq('session_id', sessionId);

  if (countError) throw new Error('Error verificando participantes');
  if ((currentParticipants || 0) >= session.max_participants) {
    throw new Error('Este desafío ya está lleno');
  }

  const { error } = await supabase
    .from('stripe_challenge_participants')
    .insert({
      session_id: sessionId,
      user_id: user.id,
      score: 0
    });

  if (error) throw error;
}

export async function submitChallengeResults(
  sessionId: string,
  score: number,
  totalQuestions: number,
  timeTaken: number
): Promise<void> {
  const user = getCurrentUser();
  if (!user) throw new Error('Usuario no autenticado');

  // Get the challenge session to fetch reward_points
  const { data: session, error: sessionError } = await supabase
    .from('stripe_challenge_sessions')
    .select('reward_points')
    .eq('id', sessionId)
    .single();

  if (sessionError) throw sessionError;
  if (!session) throw new Error('Challenge session not found');

  // Use the exercise score directly for leaderboard (correct answers + speed bonus)
  const pointsForLeaderboard = score;
  
  // Maximum possible exercise score is totalQuestions + 15 (max time bonus)
  const maxPossibleExerciseScore = totalQuestions + 15;

  const { error: updateError } = await supabase
    .from('stripe_challenge_participants')
    .update({
      score,
      completion_time: timeTaken,
      completed_at: new Date().toISOString(),
      points_earned: pointsForLeaderboard
    })
    .eq('session_id', sessionId)
    .eq('user_id', user.id);

  if (updateError) throw updateError;

  // Award streak if user scored 80% or more of the maximum possible exercise score
  const streakThreshold = Math.round(maxPossibleExerciseScore * 0.8);

  const { error: leaderError } = await supabase.rpc('update_leader_points', {
    p_user_id: user.id,
    p_points: pointsForLeaderboard,
    p_streak_increment: score >= streakThreshold ? 1 : 0
  });

  if (leaderError) throw leaderError;
}

export async function getUserChallengeSession(userId: string): Promise<{
  sessionId: string;
  songId: string;
  expiresAt: string;
} | null> {
  const { data, error } = await supabase
    .from('stripe_challenge_participants')
    .select(`
      session_id,
      session:stripe_challenge_sessions(song_id, expires_at, status)
    `)
    .eq('user_id', userId)
    .is('completed_at', null)
    .maybeSingle();

  if (error) throw error;

  if (data && (data as any).session) {
    return {
      sessionId: (data as any).session_id,
      songId: (data as any).session.song_id,
      expiresAt: (data as any).session.expires_at
    };
  }

  return null;
}

export async function getChallengeParticipants(sessionId: string): Promise<ChallengeParticipant[]> {
  const { data, error } = await supabase
    .from('stripe_challenge_participants')
    .select(`
      *,
      user:stripeusers(email, full_name, username, profile_image)
    `)
    .eq('session_id', sessionId)
    .order('score', { ascending: false })
    .order('completion_time', { ascending: true });

  if (error) throw error;
  return (data || []) as ChallengeParticipant[];
}

// ---------- Leaderboard ----------

export async function getLeaderboard(limit = 50): Promise<LeaderboardEntry[]> {
  // Try RPC first (SECURITY DEFINER) — returns joined rows with user fields
  try {
    const { data: rpcData, error: rpcError } = await supabase
      .rpc('get_public_leaderboard', { p_limit: limit });

    if (!rpcError && Array.isArray(rpcData)) {
      return rpcData.map((row: any) => ({
        id: row.id,
        user_id: row.user_id,
        total_points: row.total_points,
        challenges_completed: row.challenges_completed,
        current_streak: row.current_streak,
        best_streak: row.best_streak,
        last_activity: row.last_activity,
        user: {
          // ensure both full_name and profile_image_url are present when available
          username: row.username ?? null,
          profile_image: row.profile_image ?? null,
          profile_image_url: row.profile_image_url ?? row.profile_image ?? null,
        }
      })) as LeaderboardEntry[];
    }
  } catch (err) {
    // ignore and fallback to client-side select
  }

  // Fallback: explicit select including stripeusers fields (ensure full_name and profile_image_url)
  const { data, error } = await supabase
    .from('stripe_leaders')
    .select(`
      id,
      user_id,
      total_points,
      challenges_completed,
      current_streak,
      best_streak,
      last_activity,
      stripeusers (
        username,
        full_name,
        profile_image
      )
    `)
    .order('total_points', { ascending: false })
    .range(0, Math.max(0, limit - 1));

  if (error) throw error;

  return (data || []).map((row: any) => {
    const u = row.stripeusers ?? row.user ?? {};
    return {
      id: row.id,
      user_id: row.user_id,
      total_points: row.total_points,
      challenges_completed: row.challenges_completed,
      current_streak: row.current_streak,
      best_streak: row.best_streak,
      last_activity: row.last_activity,
      user: {
        username: u.username ?? null,
        profile_image: u.profile_image ?? null,
        full_name: u.full_name ?? null,
        profile_image_url: u.profile_image_url ?? u.profile_image ?? null,
      }
    } as LeaderboardEntry;
  });
}

/**
 * Returns the current user's rank and stats.
 * Rank is computed as: count of users with strictly higher total_points + 1.
 */
export async function getUserRank(userId: string): Promise<UserRank | null> {
  if (!userId) return null;

  // 1) Get the user's row
  const { data: me, error: meErr } = await supabase
    .from('stripe_leaders')
    .select('user_id, total_points, challenges_completed, current_streak, best_streak, last_activity')
    .eq('user_id', userId)
    .maybeSingle();

  if (meErr) throw meErr;
  if (!me) return null;

  const myPoints = Number((me as any).total_points ?? 0);

  // 2) Count how many have strictly higher points
  const { count, error: cntErr } = await supabase
    .from('stripe_leaders')
    .select('id', { count: 'exact', head: true })
    .gt('total_points', myPoints);

  if (cntErr) throw cntErr;

  const betterCount = typeof count === 'number' ? count : 0;
  const rank = betterCount + 1;

  return {
    userId,
    rank,
    totalPoints: myPoints,
    challengesCompleted: Number((me as any).challenges_completed ?? 0),
    currentStreak: Number((me as any).current_streak ?? 0),
    bestStreak: Number((me as any).best_streak ?? 0),
    lastActivity: ((me as any).last_activity as string) ?? null
  };
}